import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { push } from 'connected-react-router';
import { FormattedMessage, FormattedHTMLMessage, injectIntl } from 'react-intl';
import queryString from 'query-string';
import classNames from 'classnames';
import MathJax from 'react-mathjax2';

import datefnsParse from 'date-fns/parse';
import datefnsAddSeconds from 'date-fns/add_seconds';
import datefnsDifferenceInSeconds from 'date-fns/difference_in_seconds';

import { PRACTICE_STUDENT_ID } from '../common/enums';

import {
  UserPropType,
  CompetitionPropType,
  SeriesPropType,
  QuestionPropType,
  ChoicePropType,
  AnswerPropType,
} from '../common/types';

import {
  actions as userActions,
  selectors as userSelectors,
} from '../../store/user';

import {
  actions as competitionsActions,
  selectors as competitionsSelectors,
} from '../../store/competitions';

import {
  actions as seriesesActions,
  selectors as seriesesSelectors,
} from '../../store/serieses';

import {
  actions as questionsActions,
  selectors as questionsSelectors,
} from '../../store/questions';

import { selectors as choicesSelectors } from '../../store/choices';

import {
  actions as answersActions,
  selectors as answersSelectors,
} from '../../store/answers';

import Spinner from '../common/Spinner';
import Countdown from '../common/Countdown';
import ConfirmDialog from '../common/ConfirmDialog';
import Stats from '../management/common/Stats';

class Main extends Component {
  static propTypes = {
    intl: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
    user: UserPropType,
    competition: CompetitionPropType,
    series: SeriesPropType,
    questions: PropTypes.arrayOf(QuestionPropType),
    choices: PropTypes.arrayOf(ChoicePropType),
    answers: PropTypes.arrayOf(AnswerPropType),
    answersSeriesId: PropTypes.string,
    stats: PropTypes.object,
    fetchCompetition: PropTypes.func.isRequired,
    fetchSeries: PropTypes.func.isRequired,
    fetchQuestions: PropTypes.func.isRequired,
    fetchStats: PropTypes.func.isRequired,
    clearQuestions: PropTypes.func.isRequired,
    fetchSavedAnswers: PropTypes.func.isRequired,
    setAnswer: PropTypes.func.isRequired,
    clearAnswers: PropTypes.func.isRequired,
    logout: PropTypes.func.isRequired,
    push: PropTypes.func.isRequired,
  };

  static defaultProps = {
    user: null,
    competition: null,
    series: null,
    questions: [],
    choices: [],
    answers: [],
    answersSeriesId: null,
    stats: null,
  };

  state = {
    restoreDialogShown: false,
    canShowAnswers: true,
    hasTimeLimit: true,
    practiceStartedAt: new Date(),
    showAnswers: {},
    showAllAnswers: false,
    questionIndex: null,
    ended: false,
  };

  static getDerivedStateFromProps(props, state) {
    if (state.questionIndex !== null) return null;
    if (!props.competition || !props.series) return null;

    const { params } = props.match;
    const isPractice = params.competitionId && params.seriesId;
    const isPreview = props.match.path.endsWith('/preview');

    if (isPreview) {
      return { questionIndex: -1, showAllAnswers: false };
    }

    if (!isPractice && !props.user) return null;

    let questionIndex = -1;
    if (!isPractice) {
      if (props.competition.is_archived) {
        questionIndex = -2;
      } else if (props.competition.is_web_closed === true) {
        questionIndex = -3;
      } else if (
        props.user &&
        props.user.started_at &&
        props.series.time_limit !== null
      ) {
        const startedAt = datefnsParse(props.user.started_at);
        const target = datefnsAddSeconds(startedAt, props.series.time_limit);
        const seconds = datefnsDifferenceInSeconds(target, new Date());

        if (seconds < 0) {
          questionIndex = -3;
        }
      }
    }

    return {
      questionIndex,
      showAllAnswers: !isPractice && props.competition.is_archived,
    };
  }

  componentDidMount() {
    const {
      location,
      match,
      user,
      fetchCompetition,
      fetchSeries,
      fetchQuestions,
      fetchSavedAnswers,
    } = this.props;

    const competitionId =
      this.isPractice() || this.isPreview()
        ? match.params.competitionId
        : user.competition_permissions[0];

    const seriesId =
      this.isPractice() || this.isPreview()
        ? match.params.seriesId
        : user.series_id;

    fetchCompetition(competitionId);
    fetchSeries({ competitionId, id: seriesId });

    fetchQuestions({
      competitionId,
      seriesId,
      include:
        this.isPractice() || this.isPreview() ? 'choices' : 'choices,answers',
    });

    if (this.isPractice()) fetchSavedAnswers();

    const params = queryString.parse(location.search);
    if (params.canShowAnswers || params.hasTimeLimit) {
      this.props.push(match.url);

      if (params.canShowAnswers) {
        this.setState({ canShowAnswers: params.canShowAnswers !== 'false' });
      }

      if (params.hasTimeLimit) {
        this.setState({ hasTimeLimit: params.hasTimeLimit !== 'false' });
      }
    }

    document.addEventListener('keydown', this.onKeyDown);
  }

  componentDidUpdate() {
    const { restoreDialogShown } = this.state;
    if (restoreDialogShown || !this.isPractice()) return;

    const { answersSeriesId, series } = this.props;
    if (!series || answersSeriesId !== series.id) return;

    /* eslint-disable-next-line no-undef */
    $('#restoreAnswersConfirmDialog').modal('show');
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKeyDown);
  }

  isPractice = () => {
    const { params } = this.props.match;
    return params.competitionId && params.seriesId && !this.isPreview();
  };

  isPreview = () => {
    return this.props.match.path.endsWith('/preview');
  };

  hasEnded = () => {
    const { competition } = this.props;
    const { ended } = this.state;

    if (ended) return true;

    return (
      !this.isPractice() &&
      !this.isPreview() &&
      competition &&
      competition.is_web_closed !== false
    );
  };

  onKeyDown = e => {
    const { questionIndex } = this.state;
    const { key } = e;

    // Without this, the key press is sometimes registered twice
    e.stopImmediatePropagation();

    if (key === 'ArrowLeft') {
      e.preventDefault(); // stop arrow keys from scrolling

      if (questionIndex > 0) {
        this.onQuestionIndexChange(null, questionIndex - 1);
      }
    } else if (key === 'ArrowRight') {
      e.preventDefault(); // stop arrow keys from scrolling
      this.onQuestionIndexChange(null, questionIndex + 1);
    }
  };

  onQuestionIndexChange = (e, questionIndex) => {
    if (e) {
      e.preventDefault();
      e.currentTarget.blur();
    }

    const { questions } = this.props;
    if (questionIndex >= -1 && questionIndex < questions.length) {
      this.setState({ questionIndex });
    }
  };

  onChoose = (e, questionId, choiceId) => {
    const { competition, series } = this.props;

    if (e) {
      e.preventDefault();
      e.currentTarget.blur();
    }

    if (this.hasEnded()) return;

    this.props.setAnswer({
      competitionId: competition.id,
      seriesId: series.id,
      questionId,
      choiceId,
      invalid: false,
      studentId:
        this.isPractice() || this.isPreview() ? PRACTICE_STUDENT_ID : undefined,
    });
  };

  onStop = () => {
    const { competition, series } = this.props;
    const { questionIndex } = this.state;

    if (this.isPreview()) {
      this.props.push(`/competitions/${competition.id}/series/${series.id}`);
      return;
    }

    if (!competition.is_archived) {
      this.props.logout({ initial: true, end: questionIndex !== -3 });
      return;
    }

    if (questionIndex === -2) {
      this.props.push(this.isPractice() ? '/home/archive' : '/');
      return;
    }

    if (this.isPractice()) {
      this.props.fetchStats({
        competitionId: competition.id,
        id: series.id,
        compare_points: this.getPoints(),
      });
    }

    this.setState({
      ended: true,
      questionIndex: -2,
      showAllAnswers: true,
    });
  };

  onTimeEnd = () => {
    const { competition, series } = this.props;

    if (this.isPractice()) {
      this.props.fetchStats({
        competitionId: competition.id,
        id: series.id,
        compare_points: this.getPoints(),
      });
    } else {
      this.props.clearQuestions();
    }

    this.setState({ ended: true, questionIndex: this.isPractice() ? -2 : -3 });
  };

  onShow = questionId => {
    const { showAnswers } = this.state;
    this.setState({
      showAnswers: { ...showAnswers, [questionId]: !showAnswers[questionId] },
    });
  };

  getStudentId = () => {
    const { user } = this.props;
    return this.isPractice() || this.isPreview()
      ? PRACTICE_STUDENT_ID
      : user.id;
  };

  getPoints = () => {
    const { series, questions, answers } = this.props;
    if (!series) return null;

    return questions.reduce((points, question) => {
      const answer = answers.find(
        a =>
          a.student_id === this.getStudentId() && a.question_id === question.id
      );

      if (!answer) return points + question.skip_points;
      return (
        points +
        (answer.choice_id === question.correct_choice
          ? question.correct_points
          : question.incorrect_points)
      );
    }, series.base_points);
  };

  getStreak = () => {
    const { questions, answers } = this.props;
    const input = questions.map(q => {
      const answer = answers.find(
        a => a.student_id === this.getStudentId() && a.question_id === q.id
      );

      return answer && answer.choice_id === q.correct_choice;
    });

    const streaks = [];
    streaks.push(
      input.reduce((acc, val) => {
        if (val) return acc + 1;

        streaks.push(acc);
        return 0;
      }, 0)
    );

    return Math.max(...streaks);
  };

  renderQuestionIcon = (question, index) => {
    const { answers } = this.props;
    const { questionIndex } = this.state;

    const hasAnswered = answers.find(a => a.question_id === question.id);
    const inactiveColor = hasAnswered ? 'text-success' : 'text-muted';

    if (index === questionIndex) {
      return (
        <span
          key={question.id}
          className={classNames(
            'Student-bar-question',
            'mx-1',
            'text-primary',
            {
              'Student-bar-question-answered': hasAnswered,
              'Student-bar-question-unanswered': !hasAnswered,
            }
          )}
        >
          <span className="fa-stack">
            <i className="fas fa-circle fa-stack-2x" />
            <strong className="d-flex align-items-center justify-content-center text-white fa-stack-1x">
              {question.number}
            </strong>
          </span>
        </span>
      );
    }

    return (
      <a
        key={question.id}
        href="#"
        onClick={e => this.onQuestionIndexChange(e, index)}
        className={classNames('Student-bar-question', 'mx-1', inactiveColor, {
          'Student-bar-question-answered': hasAnswered,
          'Student-bar-question-unanswered': !hasAnswered,
        })}
      >
        <span className="fa-stack">
          <i className="far fa-circle fa-stack-2x" />
          <strong className="d-flex align-items-center justify-content-center fa-stack-1x">
            {question.number}
          </strong>
        </span>
      </a>
    );
  };

  render() {
    const {
      competition,
      series,
      user,
      questions,
      choices,
      answers,
      stats,
    } = this.props;

    const { locale } = this.props.intl;

    const {
      canShowAnswers,
      hasTimeLimit,
      practiceStartedAt,
      showAnswers,
      showAllAnswers,
      questionIndex,
    } = this.state;

    const activeQuestion = !!questions.length && questions[questionIndex];

    const activeChoices =
      activeQuestion &&
      choices.filter(c => c.question_id === activeQuestion.id);

    const activeAnswer =
      activeQuestion && answers.find(a => a.question_id === activeQuestion.id);

    const startedAt =
      this.isPractice() || this.isPreview()
        ? practiceStartedAt
        : datefnsParse(user.started_at);

    const ended = this.hasEnded();

    if (!competition) {
      return (
        <div className="Student">
          <Spinner center />
        </div>
      );
    }

    return (
      <div className="Student">
        <ConfirmDialog
          id="stopConfirmDialog"
          icon={<i className="fas fa-question-circle fa-3x text-primary" />}
          text={
            <FormattedHTMLMessage
              id="student-stop-confirm"
              defaultMessage="Oletko varma että haluat lopettaa? Voit halutessasi palata kilpailuun vielä lopettamisen jälkeenkin."
            />
          }
          onConfirm={this.onStop}
        />

        <ConfirmDialog
          id="restoreAnswersConfirmDialog"
          icon={<i className="fas fa-question-circle fa-3x text-primary" />}
          text={
            <FormattedHTMLMessage
              id="student-restore-saved-answers"
              defaultMessage="Olet keskeyttänyt tämän harjoituskilpailun aiemmin. Haluatko jatkaa vai aloittaa alusta?"
            />
          }
          cancelButtonText={
            <FormattedHTMLMessage
              id="student-restore-saved-answers-continue"
              defaultMessage="Jatka"
            />
          }
          confirmButtonText={
            <FormattedHTMLMessage
              id="student-restore-saved-answers-restart"
              defaultMessage="Aloita alusta"
            />
          }
          onCancel={() => this.setState({ restoreDialogShown: true })}
          onConfirm={() => {
            this.setState({ restoreDialogShown: true });
            this.props.clearAnswers();
          }}
        />

        <div className="Student-bar d-flex justify-content-center py-2">
          <div className="container px-1 px-md-3">
            <div className="row">
              <div className="col-sm-3">
                {questionIndex !== -3 && (
                  <button
                    type="button"
                    className="btn btn-primary btn-block my-0"
                    onClick={() => this.onQuestionIndexChange(null, -1)}
                  >
                    <i className="fas fa-info-circle fa-lg mr-3" />

                    <FormattedMessage
                      id="student-guide-button"
                      defaultMessage="Ohjeet"
                    />
                  </button>
                )}
              </div>

              {this.isPreview() ? (
                <div className="col-sm px-0 my-4 my-sm-0 d-flex justify-content-center align-items-center">
                  <span className="badge badge-primary mt-1">
                    <FormattedMessage
                      id="student-preview-message"
                      defaultMessage="Esikatselutila"
                    />
                  </span>
                </div>
              ) : (
                <div className="col-sm px-0 my-4 my-sm-0 d-flex justify-content-center align-items-center">
                  {ended ? (
                    <span className="badge badge-danger mt-1">
                      <FormattedMessage
                        id="countdown-end"
                        defaultMessage="Päättynyt"
                      />
                    </span>
                  ) : (
                    <>
                      {series && series.time_limit !== null && hasTimeLimit && (
                        <Countdown
                          target={datefnsAddSeconds(
                            startedAt,
                            series.time_limit
                          )}
                          finished={
                            <span className="badge badge-danger mt-1">
                              <FormattedMessage
                                id="countdown-end"
                                defaultMessage="Päättynyt"
                              />
                            </span>
                          }
                          onEnd={this.onTimeEnd}
                        />
                      )}

                      {((series && series.time_limit === null) ||
                        !hasTimeLimit) && (
                        <FormattedMessage
                          id="student-no-time-limit"
                          defaultMessage="Ei aikarajaa"
                        />
                      )}
                    </>
                  )}
                </div>
              )}

              <div className="col-sm-6 col-md-5 col-lg-3 text-right">
                {this.isPractice() && !ended && (
                  <button
                    type="button"
                    className="btn btn-primary btn-block my-0"
                    onClick={this.onStop}
                  >
                    <i className="fas fa-check-circle fa-lg mr-3" />
                    <FormattedMessage
                      id="student-practice-stop-button"
                      defaultMessage="Lopeta ja tarkista pisteet"
                    />
                  </button>
                )}

                {this.isPreview() && (
                  <button
                    type="button"
                    className="btn btn-primary btn-block my-0"
                    onClick={this.onStop}
                  >
                    <i className="fas fa-check-circle fa-lg mr-3" />
                    <FormattedMessage
                      id="student-preview-stop-button"
                      defaultMessage="Valmis"
                    />
                  </button>
                )}

                {!this.isPreview() &&
                  competition.is_archived &&
                  ended &&
                  questionIndex !== -2 && (
                    <button
                      type="button"
                      className="btn btn-primary btn-block my-0"
                      onClick={this.onStop}
                    >
                      <i className="fas fa-check-circle fa-lg mr-3" />
                      <FormattedMessage
                        id="student-summary-button"
                        defaultMessage="Yhteenvetosivulle"
                      />
                    </button>
                  )}

                {!this.isPreview() &&
                  (!competition.is_archived || questionIndex === -2) && (
                    <button
                      type="button"
                      className="btn btn-primary btn-block my-0"
                      data-toggle="modal"
                      data-target="#stopConfirmDialog"
                    >
                      <i className="fas fa-check-circle fa-lg mr-3" />
                      <FormattedMessage
                        id="student-stop-button"
                        defaultMessage="Valmis"
                      />
                    </button>
                  )}
              </div>
            </div>

            {!!questions.length && (
              <div className="row mt-3">
                <div className="col-12 col-xl-8 offset-xl-2 d-flex flex-wrap justify-content-center align-items-center">
                  {questions.map(this.renderQuestionIcon)}
                </div>
              </div>
            )}
          </div>
        </div>

        {questionIndex === -1 && series && (
          <div className="px-2 px-md-3 pt-3">
            <div className="container px-0 px-md-3">
              <h5 className="title-bg mt-0">
                <FormattedMessage
                  id="student-guide-general"
                  defaultMessage="Yleisohjeet"
                />
              </h5>
              <div className="px-2">
                <p>
                  <FormattedMessage
                    id="student-guide-general-1"
                    defaultMessage="Yläpalkissa näet vasemmalla infonapin. Sitä painamalla pääset tälle ohjesivulle."
                  />
                </p>

                <p>
                  <FormattedMessage
                    id="student-guide-general-2"
                    defaultMessage="Keskellä on sarjan kysymykset. Klikkaamalla kuvakkeita pääset liikkumaan kysymysten välillä."
                  />
                </p>

                <p>
                  <FormattedMessage
                    id="student-guide-general-3"
                    defaultMessage="Kysymyksen kuvake näyttää tältä {icon} kun olet vastannut kysymykseen. {br} Voit vapaasti hyppiä kysymysten välillä ja vaihtaa vastauksiasi."
                    values={{
                      icon: (
                        <span
                          className="fa-stack text-success"
                          style={{ fontSize: '8px' }}
                        >
                          <i className="far fa-circle fa-stack-2x" />
                          <strong className="d-flex align-items-center justify-content-center text-success fa-stack-1x">
                            2
                          </strong>
                        </span>
                      ),
                      br: <br />,
                    }}
                  />
                </p>

                {series.time_limit !== null && (
                  <p>
                    <FormattedMessage
                      id="student-guide-general-4"
                      defaultMessage="Oikealla näet kellon joka merkkaa vastausaikaasi. Muistathan tarkistaa vastauksesi ennen ajan päättymistä!"
                    />
                  </p>
                )}

                <p>
                  <FormattedMessage
                    id="student-guide-general-5"
                    defaultMessage="Kisaa ei tarvitse erikseen päättää, voit sulkea sivuston kun olet valmis."
                  />
                </p>
              </div>

              <h5 className="title-bg">{series.name}</h5>
              <div className="px-2">
                {series.needsFallback(locale) && (
                  <div className="d-flex align-items-center text-danger mb-3">
                    <i
                      className="fas fa-exclamation-circle fa-lg mr-3"
                      style={{ marginBottom: '2px' }}
                    />

                    <FormattedMessage
                      id="series-description-missing"
                      defaultMessage="Sarjan kuvaus puuttuu haluamallasi kielellä"
                    />
                  </div>
                )}

                <MathJax.Text text={series.descriptionWithFallback(locale)} />
              </div>

              <button
                type="button"
                className="btn btn-block btn-primary mt-5"
                onClick={() => this.onQuestionIndexChange(null, 0)}
              >
                <FormattedMessage
                  id="student-start"
                  defaultMessage="Siirry kysymyksiin"
                />
              </button>
            </div>
          </div>
        )}

        {activeQuestion && (
          <div className="px-2 px-md-3 pt-3">
            <div className="container px-0 px-md-3">
              <div className="row mx-1 mb-3 title-bg">
                <div className="col-auto pl-0">
                  <button
                    type="button"
                    className={classNames(
                      'Student-move-left',
                      'btn',
                      'btn-sm',
                      'btn-round',
                      'btn-icon',
                      'btn-transparent',
                      'd-flex',
                      'align-items-center',
                      'justify-content-center',
                      'my-0',
                      {
                        invisible: questionIndex <= 0,
                      }
                    )}
                    onClick={() =>
                      this.onQuestionIndexChange(null, questionIndex - 1)
                    }
                  >
                    <i
                      className="fas fa-chevron-left"
                      style={{ marginRight: '2px' }}
                    />
                  </button>
                </div>

                <div className="col d-flex align-items-center justify-content-center">
                  <h5 className="m-0 p-0 d-none d-md-block">
                    <FormattedMessage
                      id="student-question-header"
                      defaultMessage="Kysymys #{number} ({points} p)"
                      values={{
                        number: activeQuestion.number,
                        points: activeQuestion.correct_points,
                      }}
                    />
                  </h5>

                  <span className="d-block d-md-none">
                    <FormattedMessage
                      id="student-question-header"
                      defaultMessage="Kysymys #{number} ({points} p)"
                      values={{
                        number: activeQuestion.number,
                        points: activeQuestion.correct_points,
                      }}
                    />
                  </span>
                </div>

                <div className="col-auto pr-0">
                  <button
                    type="button"
                    className={classNames(
                      'Student-move-right',
                      'btn',
                      'btn-sm',
                      'btn-round',
                      'btn-icon',
                      'btn-transparent',
                      'd-flex',
                      'align-items-center',
                      'justify-content-center',
                      'my-0',
                      {
                        invisible: questionIndex >= questions.length - 1,
                      }
                    )}
                    onClick={() =>
                      this.onQuestionIndexChange(null, questionIndex + 1)
                    }
                  >
                    <i
                      className="fas fa-chevron-right"
                      style={{ marginLeft: '2px' }}
                    />
                  </button>
                </div>
              </div>

              {(activeQuestion.descriptionNeedsFallback(locale) ||
                activeChoices.some(c => c.needsFallback(locale))) && (
                <div className="d-flex align-items-center text-danger mb-3">
                  <i
                    className="fas fa-exclamation-circle fa-lg mr-3"
                    style={{ marginBottom: '2px' }}
                  />

                  <FormattedMessage
                    id="question-missing-elements"
                    defaultMessage="Tästä kysymyksestä puuttuu osia haluamallasi kielellä"
                  />
                </div>
              )}

              <div className="mb-4">
                <MathJax.Text
                  text={activeQuestion.descriptionWithFallback(locale)}
                />
              </div>

              {activeChoices.map((choice, index) => (
                /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
                <div
                  key={choice.id}
                  onClick={() =>
                    this.onChoose(null, activeQuestion.id, choice.id)
                  }
                  className={classNames(
                    'Student-choice',
                    'row',
                    'border',
                    'mx-0',
                    'mb-2',
                    {
                      disabled: ended,
                      correct:
                        (showAnswers[activeQuestion.id] || showAllAnswers) &&
                        activeQuestion.correct_choice === choice.id,
                      active:
                        activeAnswer && activeAnswer.choice_id === choice.id,
                    }
                  )}
                >
                  <div className="col-auto d-flex align-items-center py-2 border-right">
                    <span>{String.fromCharCode(97 + index)}</span>
                  </div>

                  <div className="col py-2">
                    <MathJax.Text
                      text={choice.descriptionWithFallback(locale)}
                    />
                  </div>
                </div>
              ))}

              {!ended && (
                <>
                  {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                  <div
                    onClick={() => this.onChoose(null, activeQuestion.id, null)}
                    className={classNames(
                      'Student-choice',
                      'Student-clear-answer',
                      'row',
                      'border',
                      'mx-0',
                      'mb-2'
                    )}
                  >
                    <div className="col-auto d-flex align-items-center py-2 border-right">
                      <span>
                        <i
                          className="fas fa-eraser"
                          style={{ marginLeft: '-2px' }}
                        />
                      </span>
                    </div>

                    <div className="col py-2">
                      <FormattedMessage
                        id="student-clear-answer"
                        defaultMessage="Tyhjennä vastaus"
                      />
                    </div>
                  </div>
                </>
              )}

              {this.isPractice() &&
                canShowAnswers &&
                !showAllAnswers &&
                !ended && (
                  <>
                    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                    <div
                      onClick={() => this.onShow(activeQuestion.id)}
                      className={classNames(
                        'Student-choice',
                        'Student-toggle-answer',
                        'row',
                        'border',
                        'mx-0',
                        'mb-2',
                        { disabled: ended }
                      )}
                    >
                      <div className="col-auto d-flex align-items-center py-2 border-right">
                        {showAnswers[activeQuestion.id] ? (
                          <span key={`${activeQuestion.id}-hide-icon`}>
                            <i
                              className="far fa-eye-slash"
                              style={{ marginLeft: '-5px' }}
                            />
                          </span>
                        ) : (
                          <span key={`${activeQuestion.id}-show-icon`}>
                            <i
                              className="far fa-eye"
                              style={{ marginLeft: '-4px' }}
                            />
                          </span>
                        )}
                      </div>

                      <div className="col py-2">
                        {showAnswers[activeQuestion.id] ? (
                          <FormattedMessage
                            id="student-hide-answer"
                            defaultMessage="Piilota ratkaisu"
                          />
                        ) : (
                          <FormattedMessage
                            id="student-show-answer"
                            defaultMessage="Näytä ratkaisu"
                          />
                        )}
                      </div>
                    </div>
                  </>
                )}

              {(showAnswers[activeQuestion.id] || showAllAnswers) && (
                <div className="mt-4">
                  {activeAnswer &&
                    activeQuestion.correct_choice ===
                      activeAnswer.choice_id && (
                      <h5 className="text-center">
                        <span className="text-success mr-3">
                          <i className="fas fa-check-circle fa-lg " />
                        </span>

                        <FormattedMessage
                          id="student-correct-answer"
                          defaultMessage="Vastasit oikein!"
                        />
                      </h5>
                    )}

                  {activeAnswer &&
                    activeQuestion.correct_choice !==
                      activeAnswer.choice_id && (
                      <h5 className="text-center">
                        <span className="text-danger mr-3">
                          <i className="fas fa-times-circle fa-lg " />
                        </span>

                        <FormattedMessage
                          id="student-incorrect-answer"
                          defaultMessage="Vastasit väärin"
                        />
                      </h5>
                    )}

                  <div className="row mx-1 mt-4 mb-3 title-bg">
                    <div className="col d-flex align-items-center justify-content-center">
                      <h5 className="m-0 p-0 d-none d-md-block">
                        <FormattedMessage
                          id="student-example"
                          defaultMessage="Esimerkkiratkaisu"
                        />
                      </h5>
                    </div>
                  </div>

                  {activeQuestion.exampleNeedsFallback(locale) && (
                    <div className="text-danger mb-3">
                      <i
                        className="fas fa-exclamation-circle mr-2"
                        style={{ marginBottom: '2px' }}
                      />

                      <FormattedMessage
                        id="question-example-missing"
                        defaultMessage="Esimerkkiratkaisu puuttuu haluamallasi kielellä"
                      />
                    </div>
                  )}

                  <MathJax.Text
                    text={activeQuestion.exampleWithFallback(locale)}
                  />
                </div>
              )}

              <div className="row mt-3 justify-content-between">
                <div className="col-auto">
                  {questionIndex > 0 && (
                    <button
                      type="button"
                      className={classNames(
                        'Student-bottom-move-left',
                        'btn',
                        'btn-primary',
                        {
                          'pointer-events-none': questionIndex <= 0,
                        }
                      )}
                      onClick={() =>
                        this.onQuestionIndexChange(null, questionIndex - 1)
                      }
                    >
                      <i
                        className="fas fa-chevron-left mr-3"
                        style={{ marginBottom: '1px' }}
                      />
                      <FormattedMessage
                        id="student-previous"
                        defaultMessage="Edellinen"
                      />
                    </button>
                  )}
                </div>

                <div className="col-auto">
                  {questionIndex < questions.length - 1 && (
                    <button
                      type="button"
                      className={classNames(
                        'Student-bottom-move-right',
                        'btn',
                        'btn-primary',
                        {
                          'pointer-events-none':
                            questionIndex >= questions.length - 1,
                        }
                      )}
                      onClick={() =>
                        this.onQuestionIndexChange(null, questionIndex + 1)
                      }
                    >
                      <FormattedMessage
                        id="student-next"
                        defaultMessage="Seuraava"
                      />
                      <i
                        className="fas fa-chevron-right ml-3"
                        style={{ marginBottom: '1px' }}
                      />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {questionIndex === -2 && (
          <div className="px-2 px-md-3 pt-3">
            <div className="container px-0 px-md-3">
              <h5 className="title-bg mt-0">
                <FormattedMessage
                  id="student-summary-answers"
                  defaultMessage="Vastauksesi"
                />
              </h5>

              <div className="px-2">
                <table className="table table-border">
                  <thead>
                    <tr>
                      {questions.map(question => (
                        <th key={question.id} className="text-center">
                          {question.number}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {questions.map(question => {
                        const answer = answers.find(
                          a =>
                            a.student_id === this.getStudentId() &&
                            a.question_id === question.id
                        );

                        let choice = '';
                        if (answer && answer.invalid) {
                          choice = 'x';
                        } else if (answer) {
                          const choiceIndex = question.choice_ids.findIndex(
                            c => c === answer.choice_id
                          );

                          choice = String.fromCharCode(97 + choiceIndex);
                        }

                        return (
                          <td key={question.id} className="text-center">
                            <div
                              className={classNames('Group-answers-answer', {
                                correct:
                                  answer &&
                                  answer.choice_id === question.correct_choice,
                                incorrect:
                                  answer &&
                                  answer.choice_id !== question.correct_choice,
                              })}
                            >
                              {choice}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>

              <h5 className="title-bg mt-5">
                <FormattedMessage
                  id="student-summary-points-title"
                  defaultMessage="Pisteesi"
                />
              </h5>

              <div className="px-2">
                <p>
                  <FormattedHTMLMessage
                    id="student-summary-points-1"
                    defaultMessage="Kokonaispisteesi oli <b>{points, number}</b> {points, plural, one {piste} other {pistettä}} ja pisin kenguruloikkasi oli <b>{streak, number}</b>."
                    values={{
                      points: this.getPoints(),
                      streak: this.getStreak(),
                    }}
                  />
                </p>

                {this.isPractice() && (
                  <p>
                    <FormattedHTMLMessage
                      id="student-summary-points-2"
                      defaultMessage="Menestyit paremmin kuin <b>{percentage} %</b> kisaan aikoinaan osallistuneista."
                      values={{
                        percentage: stats.series
                          ? Math.round(stats.series.better_percentage * 100)
                          : '...',
                      }}
                    />
                  </p>
                )}
              </div>

              {this.isPractice() && (
                <>
                  <h5 className="title-bg mt-5">
                    <FormattedMessage
                      id="student-summary-stats"
                      defaultMessage="Tilastot"
                    />
                  </h5>

                  <div className="px-2">
                    <Stats stats={stats} base="series" showParticipation />
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {questionIndex === -3 && (
          <div className="px-2 px-md-3 pt-3">
            <div className="container px-0 px-md-3">
              {competition.is_web_closed === true ? (
                <p>
                  <FormattedMessage
                    id="student-competition-closed"
                    defaultMessage="Kilpailu on päättynyt. Tulokset julkistetaan myöhemmin, pääset katsomaan niitä kirjautumalla oppilaskoodillasi julkistuksen jälkeen. Voit sulkea ikkunan tai painaa Valmis-nappia kirjautuaksesi ulos."
                  />
                </p>
              ) : (
                <>
                  <h5 className="title-bg">
                    <FormattedMessage
                      id="student-time-limit-expired-title"
                      defaultMessage="Kilpailuaikasi on päättynyt!"
                    />
                  </h5>

                  <div className="px-2">
                    <p>
                      <FormattedMessage
                        id="student-time-limit-expired-1"
                        defaultMessage="Vastauksesi on tallennettu automaattisesti ja kilpailu on sinun osaltasi valmis. Kiitos osallistumisesta!"
                      />
                    </p>

                    <p>
                      <FormattedMessage
                        id="student-time-limit-expired-2"
                        defaultMessage="Tulokset julkistetaan myöhemmin, pääset katsomaan niitä kirjautumalla oppilaskoodillasi julkistuksen jälkeen. Voit nyt sulkea ikkunan tai painaa Valmis-nappia kirjautuaksesi ulos."
                      />
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default connect(
  state => ({
    user: userSelectors.currentUser(state),
    competitions: competitionsSelectors.competitions(state),
    serieses: seriesesSelectors.serieses(state),
    questions: questionsSelectors.questions(state),
    choices: choicesSelectors.choices(state),
    answers: answersSelectors.answers(state),
    answersSeriesId: answersSelectors.answersSeriesId(state),
    stats: seriesesSelectors.stats(state),
  }),
  dispatch =>
    bindActionCreators(
      {
        fetchCompetition: competitionsActions.fetch,
        fetchSeries: seriesesActions.fetch,
        fetchQuestions: questionsActions.fetchAll,
        fetchStats: seriesesActions.fetchStats,
        clearQuestions: questionsActions.clear,
        fetchSavedAnswers: answersActions.fetchSaved,
        setAnswer: answersActions.set,
        clearAnswers: answersActions.clear,
        logout: userActions.logout,
        push,
      },
      dispatch
    ),
  (state, dispatch, props) => ({
    ...state,
    competition:
      state.competitions &&
      state.competitions.find(
        c =>
          c.id ===
          (props.match.params.competitionId ||
            state.user.competition_permissions[0])
      ),
    series:
      state.serieses &&
      state.serieses.find(
        s => s.id === (props.match.params.seriesId || state.user.series_id)
      ),
    questions:
      state.questions &&
      state.questions.filter(
        q =>
          q.series_id === (props.match.params.seriesId || state.user.series_id)
      ),
    choices:
      state.choices &&
      state.questions &&
      state.choices.filter(c =>
        state.questions.find(q => q.id === c.question_id)
      ),
    answers:
      state.answers &&
      state.questions &&
      state.answers.filter(
        a =>
          (props.match.params.competitionId
            ? a.student_id === PRACTICE_STUDENT_ID
            : a.student_id === state.user.id) &&
          state.questions.find(q => q.id === a.question_id)
      ),
    stats: {
      series:
        state.stats &&
        state.stats[props.match.params.seriesId || state.user.series_id],
    },
    ...dispatch,
    ...props,
  })
)(injectIntl(Main));
